import { Language } from "@/i18n/languages";
import { TMessage } from "@/i18n/tt";

export enum ArticleType {
  COMPANY_NAME = "COMPANY_NAME",
  PURPOSE = "PURPOSE",
  SHARE_CAPITAL = "SHARE_CAPITAL",
  FREETEXT = "FREETEXT",
  SIGNING_AUTHORITY = "SIGNING_AUTHORITY",
  CORPORATE_ASSEMBLY = "CORPORATE_ASSEMBLY",
  DISSOLUTION = "DISSOLUTION",
  CO_OWNED_SHARES_REPRESENTATIVE = "CO_OWNED_SHARES_REPRESENTATIVE",
  REGISTERED_MUNICIPALITY = "REGISTERED_MUNICIPALITY",
  MULTIPLE_CEOS = "MULTIPLE_CEOS",
  CEO_COLLECTIVE_BODY = "CEO_COLLECTIVE_BODY",
  CEO_APPOINTMENT = "CEO_APPOINTMENT",
  GENERAL_MEETING_VENUE = "GENERAL_MEETING_VENUE",
  GENERAL_MEETING_NOTICE_DAYS = "GENERAL_MEETING_NOTICE_DAYS",
  GENERAL_MEETING_DOCUMENTS = "GENERAL_MEETING_DOCUMENTS",
  ELECTRONIC_PARTICIPATION = "ELECTRONIC_PARTICIPATION",
  ADVANCE_VOTING = "ADVANCE_VOTING",
  MEETING_CHAIR = "MEETING_CHAIR",
  GENERAL_MEETING_MAJORITY = "GENERAL_MEETING_MAJORITY",
  SHAREHOLDER_CONSENT = "SHAREHOLDER_CONSENT",
  BOARD_MEMBER_ELECTION = "BOARD_MEMBER_ELECTION",
  BOARD_TERM_MONTHS = "BOARD_TERM_MONTHS",
  BOARD_INDEFINITE_TERM = "BOARD_INDEFINITE_TERM",
  BOARD_QUORUM = "BOARD_QUORUM",
  BOARD_MAJORITY = "BOARD_MAJORITY",
  PLEDGING_PROHIBITION = "PLEDGING_PROHIBITION",
  SECURITIES_DEPOSITORY = "SECURITIES_DEPOSITORY",
  TRANSFER_RESTRICTIONS = "TRANSFER_RESTRICTIONS",
  ACQUISITION_CONSENT = "ACQUISITION_CONSENT",
  RIGHT_OF_FIRST_REFUSAL = "RIGHT_OF_FIRST_REFUSAL",
  SHAREHOLDER_ELIGIBILITY = "SHAREHOLDER_ELIGIBILITY",
  CAPITAL_INCREASE_ALLOCATION = "CAPITAL_INCREASE_ALLOCATION",
  CAPITAL_REDUCTION_REDEMPTION = "CAPITAL_REDUCTION_REDEMPTION",
  PRE_EMPTIVE_RIGHTS_PER_CLASS = "PRE_EMPTIVE_RIGHTS_PER_CLASS",
  VOTING_RIGHTS = "VOTING_RIGHTS",
  SHARE_CLASS_DIVIDEND = "SHARE_CLASS_DIVIDEND",
  SHARE_CLASS_VOTING = "SHARE_CLASS_VOTING",
  SHARE_CLASS_OTHER_RIGHTS = "SHARE_CLASS_OTHER_RIGHTS",
}

export enum CeoAppointment {
  GENERAL_MEETING = "GENERAL_MEETING",
  CORPORATE_ASSEMBLY = "CORPORATE_ASSEMBLY",
}

export const SHARE_CLASS_LETTERS = ["A", "B", "C", "D", "E"] as const;
export type ShareClassLetter = (typeof SHARE_CLASS_LETTERS)[number];

export type Municipality = { number: string; name: string };

export enum SignatureRight {
  CHAIR_ALONE = "CHAIR_ALONE",
  CEO_ALONE = "CEO_ALONE",
  BOARD_JOINTLY = "BOARD_JOINTLY",
  CHAIR_AND_ONE_MEMBER_JOINTLY = "CHAIR_AND_ONE_MEMBER_JOINTLY",
  TWO_MEMBERS_JOINTLY = "TWO_MEMBERS_JOINTLY",
  MEMBERS_SEPARATELY = "MEMBERS_SEPARATELY",
  CEO_OR_CHAIR_SEPARATELY = "CEO_OR_CHAIR_SEPARATELY",
  CEO_AND_CHAIR_JOINTLY = "CEO_AND_CHAIR_JOINTLY",
  CHAIR_ALONE_OR_TWO_MEMBERS_JOINTLY = "CHAIR_ALONE_OR_TWO_MEMBERS_JOINTLY",
  CEO_ALONE_OR_MEMBERS_SEPARATELY = "CEO_ALONE_OR_MEMBERS_SEPARATELY",
  CEO_AND_ONE_MEMBER_JOINTLY = "CEO_AND_ONE_MEMBER_JOINTLY",
  THREE_MEMBERS_JOINTLY = "THREE_MEMBERS_JOINTLY",
  CHAIR_AND_TWO_MEMBERS_JOINTLY = "CHAIR_AND_TWO_MEMBERS_JOINTLY",
}

export type SignatureRightInfo = {
  signatureRight: SignatureRight;
  label: TMessage;
  texts: Partial<Record<Language, string>>;
};

function signatureRight(
  right: SignatureRight,
  label: TMessage,
  norwegian: string,
  english: string,
): SignatureRightInfo {
  return {
    signatureRight: right,
    label,
    texts: { [Language.NO]: norwegian, [Language.EN]: english },
  };
}

export const SIGNATURE_RIGHTS: SignatureRightInfo[] = [
  signatureRight(
    SignatureRight.CHAIR_ALONE,
    { key: "brreg.Styrets leder alene" },
    "Selskapets firma tegnes av styrets leder alene.",
    "The company's signature is held by the chairperson alone.",
  ),
  signatureRight(
    SignatureRight.CEO_ALONE,
    { key: "brreg.Daglig leder alene" },
    "Selskapets firma tegnes av daglig leder alene.",
    "The company's signature is held by the CEO alone.",
  ),
  signatureRight(
    SignatureRight.BOARD_JOINTLY,
    { key: "brreg.Styret i fellesskap" },
    "Selskapets firma tegnes av styret i fellesskap.",
    "The company's signature is held by the board jointly.",
  ),
  signatureRight(
    SignatureRight.CHAIR_AND_ONE_MEMBER_JOINTLY,
    { key: "brreg.Styrets leder og ett styremedlem i fellesskap" },
    "Selskapets firma tegnes av styrets leder og ett styremedlem i fellesskap.",
    "The company's signature is held by the chairperson and one board member jointly.",
  ),
  signatureRight(
    SignatureRight.TWO_MEMBERS_JOINTLY,
    { key: "brreg.To styremedlemmer i fellesskap" },
    "Selskapets firma tegnes av to styremedlemmer i fellesskap.",
    "The company's signature is held by two board members jointly.",
  ),
  signatureRight(
    SignatureRight.MEMBERS_SEPARATELY,
    { key: "brreg.Styrets medlemmer hver for seg" },
    "Selskapets firma tegnes av styremedlemmene hver for seg.",
    "The company's signature is held by the board members individually.",
  ),
  signatureRight(
    SignatureRight.CEO_OR_CHAIR_SEPARATELY,
    { key: "brreg.Daglig leder eller styrets leder hver for seg" },
    "Selskapets firma tegnes av daglig leder eller styrets leder hver for seg.",
    "The company's signature is held by the CEO or the chairperson individually.",
  ),
  signatureRight(
    SignatureRight.CEO_AND_CHAIR_JOINTLY,
    { key: "brreg.Daglig leder og styrets leder i fellesskap" },
    "Selskapets firma tegnes av daglig leder og styrets leder i fellesskap.",
    "The company's signature is held by the CEO and the chairperson jointly.",
  ),
  signatureRight(
    SignatureRight.CHAIR_ALONE_OR_TWO_MEMBERS_JOINTLY,
    { key: "brreg.Styrets leder alene eller to styremedlemmer i fellesskap" },
    "Selskapets firma tegnes av styrets leder alene eller av to styremedlemmer i fellesskap.",
    "The company's signature is held by the chairperson alone or by two board members jointly.",
  ),
  signatureRight(
    SignatureRight.CEO_ALONE_OR_MEMBERS_SEPARATELY,
    { key: "brreg.Daglig leder alene eller styrets medlemmer hver for seg" },
    "Selskapets firma tegnes av daglig leder alene eller av styremedlemmene hver for seg.",
    "The company's signature is held by the CEO alone or by the board members individually.",
  ),
  signatureRight(
    SignatureRight.CEO_AND_ONE_MEMBER_JOINTLY,
    { key: "brreg.Daglig leder og ett styremedlem i fellesskap" },
    "Selskapets firma tegnes av daglig leder og ett styremedlem i fellesskap.",
    "The company's signature is held by the CEO and one board member jointly.",
  ),
  signatureRight(
    SignatureRight.THREE_MEMBERS_JOINTLY,
    { key: "brreg.Tre styremedlemmer i fellesskap" },
    "Selskapets firma tegnes av tre styremedlemmer i fellesskap.",
    "The company's signature is held by three board members jointly.",
  ),
  signatureRight(
    SignatureRight.CHAIR_AND_TWO_MEMBERS_JOINTLY,
    { key: "brreg.Styrets leder og to styremedlemmer i fellesskap" },
    "Selskapets firma tegnes av styrets leder og to styremedlemmer i fellesskap.",
    "The company's signature is held by the chairperson and two board members jointly.",
  ),
];

export function signatureRightInfo(right: SignatureRight): SignatureRightInfo {
  return SIGNATURE_RIGHTS.find((info) => info.signatureRight === right) ?? SIGNATURE_RIGHTS[0];
}

export type ArticleValue =
  | { kind: "yesNo"; isYes: boolean }
  | { kind: "number"; number: number }
  | { kind: "municipality"; municipality: Municipality }
  | { kind: "ceoAppointment"; appointedBy: CeoAppointment }
  | { kind: "shareClass"; shareClass: ShareClassLetter }
  | { kind: "signatureRight"; signatureRight: SignatureRight };

export type ArticleValueKind = ArticleValue["kind"] | "none";

export type LawReference = {
  section: string;
  lastSection?: string;
};

export type ArticleTypeInfo = {
  type: ArticleType;
  name: TMessage;
  description: TMessage;
  tooltip: TMessage;
  law: LawReference[];
  valueKind: ArticleValueKind;
  maxLength: number;
  defaultTitles: Partial<Record<Language, string>>;
};

const TEXT_MAX_LENGTH = 2000;
const SHORT_TEXT_MAX_LENGTH = 200;

const COMPANIES_ACT_URL = "https://lovdata.no/lov/1997-06-13-44";

function provision(
  type: ArticleType,
  name: TMessage,
  description: TMessage,
  tooltip: TMessage,
  law: LawReference[],
  defaultTitles: Partial<Record<Language, string>>,
  valueKind: ArticleValueKind = "none",
  maxLength: number = TEXT_MAX_LENGTH,
): ArticleTypeInfo {
  return { type, name, description, tooltip, law, valueKind, maxLength, defaultTitles };
}

export function lawReferenceLabel(reference: LawReference): string {
  return reference.lastSection
    ? `§§ ${reference.section}–${reference.lastSection}`
    : `§ ${reference.section}`;
}

export function lawReferenceUrl(reference: LawReference): string {
  return `${COMPANIES_ACT_URL}/§${reference.section.replaceAll(" ", "")}`;
}

export const SELECTABLE_ARTICLE_TYPES: ArticleTypeInfo[] = [
  provision(
    ArticleType.FREETEXT,
    { key: "dbot.freetext" },
    { key: "article-type.freetext.description" },
    { key: "article-type.freetext.tooltip" },
    [],
    {},
  ),
  provision(
    ArticleType.SIGNING_AUTHORITY,
    { key: "article-type.signing-authority" },
    { key: "article-type.signing-authority.description" },
    { key: "article-type.signing-authority.tooltip" },
    [{ section: "6-31" }],
    { [Language.NO]: "Signaturrett", [Language.EN]: "Signing authority" },
    "signatureRight",
    SHORT_TEXT_MAX_LENGTH,
  ),
  provision(
    ArticleType.CORPORATE_ASSEMBLY,
    { key: "article-type.corporate-assembly" },
    { key: "article-type.corporate-assembly.description" },
    { key: "article-type.corporate-assembly.tooltip" },
    [{ section: "6-35" }],
    { [Language.NO]: "Bedriftsforsamling", [Language.EN]: "Corporate assembly" },
  ),
  provision(
    ArticleType.DISSOLUTION,
    { key: "article-type.dissolution" },
    { key: "article-type.dissolution.description" },
    { key: "article-type.dissolution.tooltip" },
    [{ section: "16-1" }, { section: "2-2" }],
    { [Language.NO]: "Oppløsning", [Language.EN]: "Dissolution" },
  ),
  provision(
    ArticleType.CO_OWNED_SHARES_REPRESENTATIVE,
    { key: "article-type.co-owned-shares-representative" },
    { key: "article-type.co-owned-shares-representative.description" },
    { key: "article-type.co-owned-shares-representative.tooltip" },
    [{ section: "4-3" }],
    {
      [Language.NO]: "Representant for aksjer i sameie",
      [Language.EN]: "Representative for co-owned shares",
    },
  ),
  provision(
    ArticleType.REGISTERED_MUNICIPALITY,
    { key: "article-type.registered-municipality" },
    { key: "article-type.registered-municipality.description" },
    { key: "article-type.registered-municipality.tooltip" },
    [],
    { [Language.NO]: "Forretningskommune", [Language.EN]: "Registered municipality" },
    "municipality",
  ),
  provision(
    ArticleType.MULTIPLE_CEOS,
    { key: "article-type.multiple-ceos" },
    { key: "article-type.multiple-ceos.description" },
    { key: "article-type.multiple-ceos.tooltip" },
    [{ section: "6-2" }],
    { [Language.NO]: "Daglig leder", [Language.EN]: "Chief executive officer" },
  ),
  provision(
    ArticleType.CEO_COLLECTIVE_BODY,
    { key: "article-type.ceo-collective-body" },
    { key: "article-type.ceo-collective-body.description" },
    { key: "article-type.ceo-collective-body.tooltip" },
    [{ section: "6-2" }],
    {
      [Language.NO]: "Daglige ledere som kollektivt organ",
      [Language.EN]: "CEOs as a collective body",
    },
    "yesNo",
  ),
  provision(
    ArticleType.CEO_APPOINTMENT,
    { key: "article-type.ceo-appointment" },
    { key: "article-type.ceo-appointment.description" },
    { key: "article-type.ceo-appointment.tooltip" },
    [{ section: "6-2" }],
    { [Language.NO]: "Ansettelse av daglig leder", [Language.EN]: "Appointment of CEO" },
    "ceoAppointment",
  ),
  provision(
    ArticleType.GENERAL_MEETING_VENUE,
    { key: "article-type.general-meeting-venue" },
    { key: "article-type.general-meeting-venue.description" },
    { key: "article-type.general-meeting-venue.tooltip" },
    [{ section: "5-8" }],
    { [Language.NO]: "Sted for generalforsamling", [Language.EN]: "General meeting venue" },
    "municipality",
  ),
  provision(
    ArticleType.GENERAL_MEETING_NOTICE_DAYS,
    { key: "article-type.general-meeting-notice-days" },
    { key: "article-type.general-meeting-notice-days.description" },
    { key: "article-type.general-meeting-notice-days.tooltip" },
    [{ section: "5-10" }],
    { [Language.NO]: "Innkallingsfrist", [Language.EN]: "Notice period" },
    "number",
  ),
  provision(
    ArticleType.GENERAL_MEETING_DOCUMENTS,
    { key: "article-type.general-meeting-documents" },
    { key: "article-type.general-meeting-documents.description" },
    { key: "article-type.general-meeting-documents.tooltip" },
    [{ section: "5-11 a" }],
    {
      [Language.NO]: "Tilgjengeliggjøring av dokumenter",
      [Language.EN]: "Availability of meeting documents",
    },
  ),
  provision(
    ArticleType.ELECTRONIC_PARTICIPATION,
    { key: "article-type.electronic-participation" },
    { key: "article-type.electronic-participation.description" },
    { key: "article-type.electronic-participation.tooltip" },
    [{ section: "5-8" }],
    { [Language.NO]: "Elektronisk deltakelse", [Language.EN]: "Electronic participation" },
  ),
  provision(
    ArticleType.ADVANCE_VOTING,
    { key: "article-type.advance-voting" },
    { key: "article-type.advance-voting.description" },
    { key: "article-type.advance-voting.tooltip" },
    [{ section: "5-11 b" }],
    { [Language.NO]: "Forhåndsstemme", [Language.EN]: "Advance voting" },
  ),
  provision(
    ArticleType.MEETING_CHAIR,
    { key: "article-type.meeting-chair" },
    { key: "article-type.meeting-chair.description" },
    { key: "article-type.meeting-chair.tooltip" },
    [{ section: "5-12" }],
    { [Language.NO]: "Møteleder", [Language.EN]: "Chair of the meeting" },
    "none",
    SHORT_TEXT_MAX_LENGTH,
  ),
  provision(
    ArticleType.GENERAL_MEETING_MAJORITY,
    { key: "article-type.general-meeting-majority" },
    { key: "article-type.general-meeting-majority.description" },
    { key: "article-type.general-meeting-majority.tooltip" },
    [{ section: "5-17" }, { section: "5-18" }],
    {
      [Language.NO]: "Flertallskrav i generalforsamlingen",
      [Language.EN]: "Majority requirements at the general meeting",
    },
  ),
  provision(
    ArticleType.SHAREHOLDER_CONSENT,
    { key: "article-type.shareholder-consent" },
    { key: "article-type.shareholder-consent.description" },
    { key: "article-type.shareholder-consent.tooltip" },
    [{ section: "5-23" }],
    { [Language.NO]: "Samtykke fra aksjeeiere", [Language.EN]: "Shareholder consent" },
  ),
  provision(
    ArticleType.BOARD_MEMBER_ELECTION,
    { key: "article-type.board-member-election" },
    { key: "article-type.board-member-election.description" },
    { key: "article-type.board-member-election.tooltip" },
    [{ section: "6-3" }],
    { [Language.NO]: "Valg av styremedlemmer", [Language.EN]: "Election of board members" },
  ),
  provision(
    ArticleType.BOARD_TERM_MONTHS,
    { key: "article-type.board-term-months" },
    { key: "article-type.board-term-months.description" },
    { key: "article-type.board-term-months.tooltip" },
    [{ section: "6-6" }],
    { [Language.NO]: "Styrets tjenestetid", [Language.EN]: "Board term of office" },
    "number",
  ),
  provision(
    ArticleType.BOARD_INDEFINITE_TERM,
    { key: "article-type.board-indefinite-term" },
    { key: "article-type.board-indefinite-term.description" },
    { key: "article-type.board-indefinite-term.tooltip" },
    [{ section: "6-6" }],
    { [Language.NO]: "Styrets tjenestetid", [Language.EN]: "Board term of office" },
  ),
  provision(
    ArticleType.BOARD_QUORUM,
    { key: "article-type.board-quorum" },
    { key: "article-type.board-quorum.description" },
    { key: "article-type.board-quorum.tooltip" },
    [{ section: "6-24" }],
    { [Language.NO]: "Styrets vedtaksførhet", [Language.EN]: "Board quorum" },
  ),
  provision(
    ArticleType.BOARD_MAJORITY,
    { key: "article-type.board-majority" },
    { key: "article-type.board-majority.description" },
    { key: "article-type.board-majority.tooltip" },
    [{ section: "6-25" }, { section: "6-26" }],
    {
      [Language.NO]: "Flertallskrav i styret",
      [Language.EN]: "Majority requirements in the board",
    },
  ),
  provision(
    ArticleType.PLEDGING_PROHIBITION,
    { key: "article-type.pledging-prohibition" },
    { key: "article-type.pledging-prohibition.description" },
    { key: "article-type.pledging-prohibition.tooltip" },
    [{ section: "4-8" }],
    { [Language.NO]: "Pantsettelse av aksjer", [Language.EN]: "Pledging of shares" },
  ),
  provision(
    ArticleType.SECURITIES_DEPOSITORY,
    { key: "article-type.securities-depository" },
    { key: "article-type.securities-depository.description" },
    { key: "article-type.securities-depository.tooltip" },
    [{ section: "4-4" }],
    {
      [Language.NO]: "Registrering i verdipapirsentral",
      [Language.EN]: "Registration in a central securities depository",
    },
  ),
  provision(
    ArticleType.TRANSFER_RESTRICTIONS,
    { key: "article-type.transfer-restrictions" },
    { key: "article-type.transfer-restrictions.description" },
    { key: "article-type.transfer-restrictions.tooltip" },
    [{ section: "4-15" }],
    { [Language.NO]: "Omsetningsbegrensninger", [Language.EN]: "Transfer restrictions" },
  ),
  provision(
    ArticleType.ACQUISITION_CONSENT,
    { key: "article-type.acquisition-consent" },
    { key: "article-type.acquisition-consent.description" },
    { key: "article-type.acquisition-consent.tooltip" },
    [{ section: "4-15" }, { section: "4-16" }, { section: "4-17" }],
    { [Language.NO]: "Samtykkekrav ved eierskifte", [Language.EN]: "Consent to acquisition" },
  ),
  provision(
    ArticleType.RIGHT_OF_FIRST_REFUSAL,
    { key: "article-type.right-of-first-refusal" },
    { key: "article-type.right-of-first-refusal.description" },
    { key: "article-type.right-of-first-refusal.tooltip" },
    [{ section: "4-15" }, { section: "4-19", lastSection: "4-23" }],
    { [Language.NO]: "Forkjøpsrett", [Language.EN]: "Right of first refusal" },
  ),
  provision(
    ArticleType.SHAREHOLDER_ELIGIBILITY,
    { key: "article-type.shareholder-eligibility" },
    { key: "article-type.shareholder-eligibility.description" },
    { key: "article-type.shareholder-eligibility.tooltip" },
    [{ section: "4-18" }],
    { [Language.NO]: "Krav til aksjeeiere", [Language.EN]: "Shareholder eligibility requirements" },
  ),
  provision(
    ArticleType.CAPITAL_INCREASE_ALLOCATION,
    { key: "article-type.capital-increase-allocation" },
    { key: "article-type.capital-increase-allocation.description" },
    { key: "article-type.capital-increase-allocation.tooltip" },
    [{ section: "10-23" }],
    {
      [Language.NO]: "Tildeling ved kapitalforhøyelse",
      [Language.EN]: "Allocation in capital increase",
    },
  ),
  provision(
    ArticleType.CAPITAL_REDUCTION_REDEMPTION,
    { key: "article-type.capital-reduction-redemption" },
    { key: "article-type.capital-reduction-redemption.description" },
    { key: "article-type.capital-reduction-redemption.tooltip" },
    [{ section: "12-7" }],
    {
      [Language.NO]: "Innløsning ved kapitalnedsettelse",
      [Language.EN]: "Redemption on capital reduction",
    },
  ),
  provision(
    ArticleType.PRE_EMPTIVE_RIGHTS_PER_CLASS,
    { key: "article-type.pre-emptive-rights-per-class" },
    { key: "article-type.pre-emptive-rights-per-class.description" },
    { key: "article-type.pre-emptive-rights-per-class.tooltip" },
    [{ section: "10-4" }],
    { [Language.NO]: "Fortrinnsrett", [Language.EN]: "Pre-emptive rights" },
  ),
  provision(
    ArticleType.VOTING_RIGHTS,
    { key: "article-type.voting-rights" },
    { key: "article-type.voting-rights.description" },
    { key: "article-type.voting-rights.tooltip" },
    [{ section: "5-3" }],
    { [Language.NO]: "Stemmerett", [Language.EN]: "Voting rights" },
  ),
  provision(
    ArticleType.SHARE_CLASS_DIVIDEND,
    { key: "article-type.share-class-dividend" },
    { key: "article-type.share-class-dividend.description" },
    { key: "article-type.share-class-dividend.tooltip" },
    [{ section: "4-1" }],
    { [Language.NO]: "Utbytterett", [Language.EN]: "Dividend rights" },
    "shareClass",
  ),
  provision(
    ArticleType.SHARE_CLASS_VOTING,
    { key: "article-type.share-class-voting" },
    { key: "article-type.share-class-voting.description" },
    { key: "article-type.share-class-voting.tooltip" },
    [{ section: "4-1" }],
    { [Language.NO]: "Stemmerett per aksjeklasse", [Language.EN]: "Voting rights per share class" },
    "shareClass",
  ),
  provision(
    ArticleType.SHARE_CLASS_OTHER_RIGHTS,
    { key: "article-type.share-class-other-rights" },
    { key: "article-type.share-class-other-rights.description" },
    { key: "article-type.share-class-other-rights.tooltip" },
    [{ section: "4-1" }],
    {
      [Language.NO]: "Andre rettigheter per aksjeklasse",
      [Language.EN]: "Other rights per share class",
    },
    "shareClass",
  ),
];

export function articleTypeInfo(type: ArticleType): ArticleTypeInfo {
  return SELECTABLE_ARTICLE_TYPES.find((info) => info.type === type) ?? SELECTABLE_ARTICLE_TYPES[0];
}

export function articleSlot(type: ArticleType, value?: ArticleValue): string | undefined {
  if (type === ArticleType.FREETEXT) {
    return undefined;
  }
  return value?.kind === "shareClass" ? `${type}:${value.shareClass}` : type;
}

export function isArticleTypeAlwaysAvailable(type: ArticleType): boolean {
  const kind = articleTypeInfo(type).valueKind;
  return type === ArticleType.FREETEXT || kind === "shareClass";
}
